import { defaultUrlMatcher, PRIMARY_OUTLET, Router, UrlTree } from "@angular/router";

// The page a signed-out admin asked for, such as an alert's "Open in Admin" link, opened when the sign-in comes back
// with ?token=. Per tab, and cleared whether or not it is used, so it never fires in a later session.
export const RETURN_PATH_KEY = "signInReturnPath";

// The query string, the fragment and matrix parameters are dropped, so token, user_id, errorcode and errordescription
// are never stored.
export function saveReturnPath(router: Router, url: string): void {
    const address = router.parseUrl(url);
    address.queryParams = {};
    address.fragment = null;
    for (const segment of address.root.children[PRIMARY_OUTLET]?.segments ?? []) {
        segment.parameters = {};
    }
    const path = router.serializeUrl(address);
    if (isInternalPath(path)) {
        sessionStorage.setItem(RETURN_PATH_KEY, path);
    }
}

export function takeReturnTarget(router: Router): true | UrlTree {
    const path: string | null = sessionStorage.getItem(RETURN_PATH_KEY);
    clearReturnPath();
    if (path === null || !isInternalPath(path)) {
        return true;
    }

    const target = router.parseUrl(path);
    return isAppRoute(router, target) ? target : true;
}

export function clearReturnPath(): void {
    sessionStorage.removeItem(RETURN_PATH_KEY);
}

// Never an absolute URL, a scheme (javascript:, http:), a protocol-relative "//" or "/\", a query, a fragment or
// matrix parameters.
function isInternalPath(path: string): boolean {
    return (
        path.startsWith("/") &&
        !path.startsWith("//") &&
        !path.startsWith("/\\") &&
        !path.includes("?") &&
        !path.includes("#") &&
        !path.includes(";")
    );
}

// Recognised by the router's own matching against the app's routes, not by comparing strings.
function isAppRoute(router: Router, target: UrlTree): boolean {
    const group = target.root.children[PRIMARY_OUTLET] ?? target.root;
    if (target.root.numberOfChildren > 1 || group.numberOfChildren > 0) {
        return false;
    }

    return router.config.some((route) => {
        const match = (route.matcher ?? defaultUrlMatcher)(group.segments, group, route);
        return match !== null && match.consumed.length === group.segments.length;
    });
}
